import type { FieldWidth, IFieldDefinition, IRecordTypeDefinition, IRecordTypeOverride } from '@platform/core/types';

const WIDTHS: readonly string[] = ['S', 'M', 'L'];
const isWidth = (w: unknown): w is FieldWidth => typeof w === 'string' && WIDTHS.includes(w);

/**
 * Явні ширини полів форми (прототип v4 layout.widths): ширина поля перевизначення простору (`fields.<ключ>.width`, ADR-023 — сервер
 * переносить її в розкладку груп форм, у «Подробицях» подвоєною) і ширина власного поля (`customFields[].width`). Чиста функція.
 */
export function explicitWidthsOf(patch: IRecordTypeOverride): Partial<Record<string, FieldWidth>> {
  const out: Partial<Record<string, FieldWidth>> = {};
  for (const [key, o] of Object.entries(patch.fields ?? {})) if (isWidth(o?.width)) out[key] = o.width;
  for (const f of patch.customFields ?? []) if (isWidth(f.width)) out[f.key] = f.width;
  return out;
}

/** Типові ширини прототипу v4 (GSET.widths): тип без власної ширини — S (посилання, файл, так/ні). */
const BY_TYPE: Partial<Record<string, FieldWidth>> = { text: 'M', textarea: 'L', number: 'S', date: 'S', select: 'S', user: 'M', tags: 'L', area: 'M', iteration: 'M' };
const SHORT_TEXT = /(^|_)(code|no|num|number|regno|ref|id|phone|zip|postcode|year|qty)$/i;
/** Найдовший варіант, після якого список стає ширшим (прототип v4 GSET.longOpt). */
const LONG_OPTION = 16;

/**
 * Ширина «Авто» поля (прототип v4 autoW = fieldWidth): власна ширина поля в опублікованому визначенні (`published`, без перевизначення
 * простору), інакше за типом — короткий текст (код, номер, телефон…) S, список із варіантом довшим за 16 знаків M, інші типи — за
 * GSET.widths, тип поза ними — S. Чиста функція.
 */
export function autoWidthOf(field: IFieldDefinition, published: IRecordTypeDefinition | undefined): FieldWidth {
  const own = published ? published.fields.find((f) => f.key === field.key)?.width : field.width;
  if (isWidth(own)) return own;
  if (field.type === 'text') return SHORT_TEXT.test(field.key) ? 'S' : 'M';
  if (field.type === 'select') return (field.options ?? []).some((o) => o.value.length > LONG_OPTION) ? 'M' : 'S';
  return BY_TYPE[field.type] ?? 'S';
}

/**
 * Ширини явних полів форми в патчі (зміна ширини в редакторі форми): поле власне — `customFields[].width`, інакше
 * `fields.<ключ>.width`; «Авто» (немає явної) вилучає ширину, порожнє перевизначення поля зникає. Чиста функція.
 */
export function withWidths(patch: IRecordTypeOverride, widths: Partial<Record<string, FieldWidth>>): IRecordTypeOverride {
  const out: IRecordTypeOverride = { ...patch };
  const custom = new Set((patch.customFields ?? []).map((f) => f.key));
  if (patch.customFields) out.customFields = patch.customFields.map((f) => { const { width: _w, ...rest } = f; return widths[f.key] ? { ...rest, width: widths[f.key]! } : rest; });
  const fields = { ...(patch.fields ?? {}) };
  for (const key of new Set([...Object.keys(fields), ...Object.keys(widths)])) {
    if (custom.has(key)) continue;
    const { width: _w, ...rest } = fields[key] ?? {};
    const width = widths[key];
    const next = width ? { ...rest, width } : rest;
    if (Object.keys(next).length) fields[key] = next;
    else delete fields[key];
  }
  if (Object.keys(fields).length) out.fields = fields;
  else delete out.fields;
  return out;
}
